import { useEffect, useState } from "react";

// Three-way theme: "light" | "dark" | "system" (follow OS prefers-
// color-scheme). Persisted in localStorage as "ud:theme". Mounts a
// `class="dark"` on <html> when the resolved theme is dark, removes
// it when light. matchMedia listener keeps "system" in sync if the
// OS preference flips while the tab is open.

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "ud:theme";

function readStored(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* ignore */
  }
  return "system";
}

function osPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

function applyClass(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

export function useTheme(): {
  theme: Theme;
  resolved: ResolvedTheme;
  setTheme: (t: Theme) => void;
} {
  const [theme, setThemeState] = useState<Theme>(() => readStored());
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    readStored() === "dark" || (readStored() === "system" && osPrefersDark())
      ? "dark"
      : "light",
  );

  useEffect(() => {
    const next: ResolvedTheme =
      theme === "dark" || (theme === "system" && osPrefersDark()) ? "dark" : "light";
    setResolved(next);
    applyClass(next);
  }, [theme]);

  // Listen for OS-level changes when in "system".
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent): void => {
      const next: ResolvedTheme = e.matches ? "dark" : "light";
      setResolved(next);
      applyClass(next);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const setTheme = (t: Theme): void => {
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      /* ignore */
    }
    setThemeState(t);
  };

  return { theme, resolved, setTheme };
}
